import React, { useMemo, useContext, useEffect, useRef } from "react"
import useLocalStorage from "hooks/useLocalStorage"
import PropTypes from "prop-types"
import {
  DataGridPro,
  GridEditModes,
  GridActionsCellItem,
} from "@mui/x-data-grid-pro"
import { Box, Tooltip } from "@mui/material"
import LinkIcon from "@mui/icons-material/Link"
import { Delete } from "@mui/icons-material"
import TypeIconWithMenu from "./components/TypeIconWithMenu"
import UnityWithMenu from "./components/UnityWithMenu"
import {
  DescriptionEditCell,
  QuantityEditCell,
  WasteEditCell,
  PriceReducedEditCell,
} from "./components/PartDetailsGridEditCells"
import CustomToolbar from "./components/CustomToolbar"
import AuthContext from "context/auth/authContext"
import i18n from "i18n"
import { calcularImporteDeDescomposicion, getCurrency } from "utils/functions"
import { isEmpty } from "lodash"
import DataGridLocaleText from "common/DataGridLocaleText"
import {
  MEASUREMENTS,
  SUB_DECOMPOSITIONS,
  TOTAL_DECOMPOSITIONS,
} from "constants/decimals"
import { useDecimalFormatter } from "hooks/useDecimalFormatter"
import useHideDisabledColumns from "hooks/useHideDisabledColumns"
import {
  buildDetailGridRow,
  buildUpdatedDetailRow,
  getNextTabTarget,
  GRID_TAB_ORDER,
  EDIT_TAB_ORDER,
  REFERENCE_PRICE_BANK,
  isPartDetailsCellEditable,
} from "./partDetails.utils"

const COLUMN_VIS_KEY = "decompositionGridConfig"

const PartDetailsDataGrid = ({
  details,
  setDetails,
  partidaState,
  bancoDePrecios,
  showDynamicElementButton = false,
  onAddEmptyDetail,
  isTender,
}) => {
  const [columnVisibilityModel, setColumnVisibilityModel] = useLocalStorage(
    COLUMN_VIS_KEY,
    { priceReduced: false, code: false },
  )
  const handleColumnVisibilityChange = React.useCallback(
    model => {
      setColumnVisibilityModel(model)
    },
    [setColumnVisibilityModel],
  )
  const { formatValue } = useDecimalFormatter()

  const { user } = useContext(AuthContext)
  const currency = getCurrency(user["custom:currency"])

  const projectId = isEmpty(bancoDePrecios)
    ? (partidaState.projectId ?? null)
    : null

  const isReferencePriceBank = bancoDePrecios === REFERENCE_PRICE_BANK

  useHideDisabledColumns()

  const rows = useMemo(() => {
    return details.map((detail, index) =>
      buildDetailGridRow(
        detail,
        index,
        i18n.t("decomposition.noDecompositions"),
      ),
    )
  }, [details])

  const gridRef = useRef(null)
  const previousRowsLengthRef = useRef(rows.length)

  useEffect(() => {
    const currentRowsLength = rows.length
    const previousRowsLength = previousRowsLengthRef.current

    if (currentRowsLength > previousRowsLength && currentRowsLength > 0) {
      requestAnimationFrame(() => {
        const api = gridRef.current?.apiRef?.current
        if (api?.scrollToIndexes) {
          api.scrollToIndexes({ rowIndex: currentRowsLength - 1 })
          return
        }

        const rootElement = api?.rootElementRef?.current ?? gridRef.current
        const virtualScroller = rootElement?.querySelector?.(
          ".MuiDataGrid-virtualScroller",
        )
        if (virtualScroller) {
          virtualScroller.scrollTop = virtualScroller.scrollHeight
        }
      })
    }

    previousRowsLengthRef.current = currentRowsLength
  }, [rows.length])

  const forceCommitActiveCell = () => {
    if (!gridRef.current) return
    try {
      const api = gridRef.current?.apiRef?.current
      if (api) {
        const cell = api.state?.focus?.cell
        if (cell) {
          api.stopCellEditMode({ id: cell.id, field: cell.field })
        }
      }
    } catch (e) {
      console.error("Error in forceCommitActiveCell:", e)
    }
  }

  const processRowUpdate = (newRow, oldRow) => {
    const { rowForStore, rowForGrid } = buildUpdatedDetailRow(newRow, oldRow)

    setDetails(prev => {
      const updated = [...prev]
      updated[newRow.id] = rowForStore
      return updated
    })

    return {
      ...rowForGrid,
      total: calcularImporteDeDescomposicion(rowForStore),
    }
  }

  const deleteDetail = index => {
    setDetails(prevArray => {
      const newArray = [...prevArray]
      newArray.splice(index, 1)
      return newArray
    })
  }

  const handleRowOrderChange = React.useCallback(
    params => {
      const { oldIndex, targetIndex } = params
      if (
        !Number.isInteger(oldIndex) ||
        !Number.isInteger(targetIndex) ||
        oldIndex === targetIndex
      ) {
        return
      }
      setDetails(prev => {
        const next = [...prev]
        const [removed] = next.splice(oldIndex, 1)
        if (removed === undefined) return prev
        next.splice(targetIndex, 0, removed)
        return next
      })
    },
    [setDetails],
  )

  const handleTypeChange = (rowId, newType) => {
    setDetails(prev => {
      const updated = [...prev]
      const currentDetail = updated[rowId]
      updated[rowId] = {
        ...currentDetail,
        type: newType,
        unity:
          newType === "coste"
            ? "%"
            : currentDetail.type === "coste" && newType !== "coste"
              ? "pa"
              : currentDetail.unity,
      }
      return updated
    })
  }

  const handleUnityChange = (rowId, newUnity) => {
    setDetails(prev => {
      const updated = [...prev]
      updated[rowId] = {
        ...updated[rowId],
        unity: newUnity,
      }
      return updated
    })
  }

  const handleEditCellTabKeyDown = React.useCallback(
    (event, currentField, params) => {
      if (event.key !== "Tab") return

      event.preventDefault()
      event.stopPropagation()

      params.api.stopCellEditMode({ id: params.id, field: currentField })

      const nextTarget = getNextTabTarget({
        currentField,
        row: params.row,
        isTender,
        isReferencePriceBank,
        tabOrder: EDIT_TAB_ORDER,
      })

      if (!nextTarget) return

      requestAnimationFrame(() => {
        if (nextTarget.action === "focus") {
          params.api.setCellFocus(params.id, nextTarget.field)
          return
        }

        params.api.startCellEditMode({ id: params.id, field: nextTarget.field })
      })
    },
    [isReferencePriceBank, isTender],
  )

  const withDynamicTooltip = (params, node, spanStyle) => {
    if (params.row.itemId == null) return node
    return (
      <Tooltip title={i18n.t("Elemento Dinámico")} arrow>
        <span style={spanStyle}>{node}</span>
      </Tooltip>
    )
  }

  const columns = useMemo(
    () => [
      {
        field: "type",
        headerName: i18n.t("Tipo"),
        width: 70,
        minWidth: 70,
        maxWidth: 70,
        editable: false,
        renderCell: params => (
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            <TypeIconWithMenu
              value={String(params.value || "")}
              onChange={newValue => handleTypeChange(params.id, newValue)}
              disabled={isTender || isReferencePriceBank}
            />
            {params.row.itemId != null && (
              <Tooltip title={i18n.t("Elemento Dinámico")} arrow>
                <span>
                  <LinkIcon fontSize="small" color="action" sx={{ ml: 0.5 }} />
                </span>
              </Tooltip>
            )}
          </Box>
        ),
      },
      {
        field: "code",
        headerName: i18n.t("Ref."),
        width: 100,
        editable: !isReferencePriceBank,
      },
      {
        field: "description",
        headerName: i18n.t("Descripción"),
        width: 250,
        editable: !isReferencePriceBank,
        flex: 1,
        renderCell: params =>
          withDynamicTooltip(params, params.value ?? "", {
            width: "100%",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }),
        renderEditCell: params => (
          <DescriptionEditCell
            {...params}
            onTabKeyDown={handleEditCellTabKeyDown}
          />
        ),
      },
      {
        field: "quantity",
        headerName: i18n.t("Cantidad"),
        width: 90,
        editable: !isTender && !isReferencePriceBank,
        type: "number",
        renderCell: params => {
          const val = parseFloat(params.value || 0)
          const shown = params.row.unity === "%" ? val * 100 : val
          return `${isNaN(shown) ? "" : formatValue(shown, MEASUREMENTS)}`
        },
        renderEditCell: params => (
          <QuantityEditCell
            {...params}
            onTabKeyDown={handleEditCellTabKeyDown}
          />
        ),
      },
      {
        field: "waste",
        headerName: i18n.t("Desperdicio"),
        width: 90,
        editable: !isReferencePriceBank,
        type: "number",
        renderCell: params => {
          const val = parseFloat(params.value || 0)
          const text = isNaN(val) ? "" : formatValue(val, MEASUREMENTS)
          return withDynamicTooltip(params, text)
        },
        renderEditCell: params => (
          <WasteEditCell {...params} onTabKeyDown={handleEditCellTabKeyDown} />
        ),
      },
      {
        field: "factor",
        headerName: i18n.t("Factor"),
        width: 90,
        editable: false,
        type: "number",
        renderCell: params => {
          const val = parseFloat(params.value || 1)
          const text = isNaN(val) ? "" : formatValue(val, MEASUREMENTS)
          return withDynamicTooltip(params, text)
        },
      },
      {
        field: "unity",
        headerName: i18n.t("Ud."),
        width: 50,
        editable: false,
        renderCell: params =>
          withDynamicTooltip(
            params,
            <UnityWithMenu
              value={String(params.value || "")}
              onBeforeOpen={forceCommitActiveCell}
              onChange={newValue => handleUnityChange(params.id, newValue)}
              disabled={
                isTender || isReferencePriceBank || params.row.type === "coste"
              }
              disabledStyle={isReferencePriceBank}
            />,
          ),
      },
      {
        field: "price",
        headerName: i18n.t("decomposition.price"),
        width: 100,
        editable: !isTender && !isReferencePriceBank,
        type: "number",
        renderCell: params => {
          return `${formatValue(params.value, SUB_DECOMPOSITIONS)} ${currency}`
        },
      },
      {
        field: "priceReduced",
        headerName:
          i18n.t("dynamicElement.columns.priceReduced") || "Precio reducido",
        width: 120,
        editable: !isReferencePriceBank,
        type: "number",
        renderCell: params => {
          const hasValue =
            params.value !== undefined &&
            params.value !== null &&
            params.value !== ""
          return hasValue
            ? `${parseFloat(params.value).toFixed(2)} ${currency}`
            : "-"
        },
        renderEditCell: params => <PriceReducedEditCell {...params} />,
      },
      {
        field: "total",
        headerName: i18n.t("Importe"),
        width: 80,
        editable: false,
        renderCell: params =>
          `${formatValue(params.value, TOTAL_DECOMPOSITIONS)} ${currency}`,
      },
      {
        field: "actions",
        headerName: "",
        width: 30,
        type: "actions",
        pinned: "right",
        disableColumnMenu: true,
        sortable: false,
        filterable: false,
        hideable: false,
        disableExport: true,
        disableColumnSelector: true,
        getActions: params => {
          if (isTender || isReferencePriceBank) {
            return []
          }
          return [
            <GridActionsCellItem
              key="delete"
              icon={<Delete />}
              label="Eliminar"
              onClick={() => deleteDetail(params.id)}
              className="delete-action-button"
            />,
          ]
        },
      },
    ],
    [
      currency,
      bancoDePrecios,
      showDynamicElementButton,
      isTender,
      handleEditCellTabKeyDown,
    ],
  )

  const ToolbarComponent = React.useCallback(
    () => (
      <CustomToolbar
        bancoDePrecios={bancoDePrecios}
        onAddEmptyDetail={onAddEmptyDetail}
        showDynamicElementButton={showDynamicElementButton}
        projectId={projectId}
        isTender={isTender}
      />
    ),
    [bancoDePrecios, onAddEmptyDetail, showDynamicElementButton, projectId],
  )

  const getRowClassName = params => {
    return params.row.itemId != null ? "row-ppe" : ""
  }

  return (
    <Box sx={{ height: "100%", width: "100%" }}>
      <DataGridPro
        ref={gridRef}
        rows={rows}
        columns={columns}
        editMode={GridEditModes.Cell}
        processRowUpdate={processRowUpdate}
        onProcessRowUpdateError={error =>
          console.error("Error updating row:", error)
        }
        rowReordering
        onRowOrderChange={handleRowOrderChange}
        isRowReorderable={() => !isTender && !isReferencePriceBank}
        slots={{
          toolbar: ToolbarComponent,
        }}
        getRowClassName={getRowClassName}
        hideFooter
        disableRowSelectionOnClick={false}
        isCellEditable={params =>
          isPartDetailsCellEditable({
            field: params.field,
            row: params.row,
            isTender,
            isReferencePriceBank,
          })
        }
        disableColumnMenu
        disableColumnSorting
        density="compact"
        initialState={{
          pinnedColumns: {
            right: ["actions"],
          },
        }}
        slotProps={{
          columnsPanel: {},
        }}
        columnVisibilityModel={columnVisibilityModel}
        onColumnVisibilityModelChange={handleColumnVisibilityChange}
        sx={{
          border: 1,
          borderColor: "divider",
          "& .MuiDataGrid-scrollbar--vertical, & .MuiDataGrid-scrollbar--horizontal":
            {
              scrollbarWidth: "thin",
              scrollbarColor: "#b7bcc6 #eef1f5",
            },
          "& .MuiDataGrid-scrollbar--vertical::-webkit-scrollbar, & .MuiDataGrid-scrollbar--horizontal::-webkit-scrollbar":
            {
              width: 10,
              height: 10,
            },
          "& .MuiDataGrid-scrollbar--vertical::-webkit-scrollbar-track, & .MuiDataGrid-scrollbar--horizontal::-webkit-scrollbar-track":
            {
              backgroundColor: "#eef1f5",
              borderRadius: 10,
            },
          "& .MuiDataGrid-scrollbar--vertical::-webkit-scrollbar-thumb, & .MuiDataGrid-scrollbar--horizontal::-webkit-scrollbar-thumb":
            {
              backgroundColor: "#b7bcc6",
              borderRadius: 10,
              border: "2px solid #eef1f5",
            },
          "& .MuiDataGrid-scrollbar--vertical::-webkit-scrollbar-thumb:hover, & .MuiDataGrid-scrollbar--horizontal::-webkit-scrollbar-thumb:hover":
            {
              backgroundColor: "#9da4b2",
            },
          "& .MuiDataGrid-scrollbar--vertical::-webkit-scrollbar-corner, & .MuiDataGrid-scrollbar--horizontal::-webkit-scrollbar-corner":
            {
              backgroundColor: "#eef1f5",
            },
          "& .MuiDataGrid-columnHeaders::-webkit-scrollbar": {
            display: "none",
          },
          "& .MuiDataGrid-rowReorderIcon": {
            color: "text.secondary",
          },
          "& .MuiDataGrid-rowReorderCell .MuiSvgIcon-root": {
            color: "text.secondary",
          },
          "& .MuiDataGrid-cell": {
            borderColor: "divider",
          },
          "& .MuiDataGrid-row.row-ppe": {
            backgroundColor: "transparent",
          },
          "& .MuiDataGrid-row": {
            "&:hover .delete-action-button": {
              opacity: 1,
            },
            "& .delete-action-button": {
              opacity: 0,
              transition: "opacity 0.2s ease-in-out",
            },
          },
          "& .MuiDataGrid-columnHeader": {
            "& .MuiDataGrid-sortIcon": {
              display: "none",
            },
            "& .MuiDataGrid-iconButtonContainer": {
              display: "none",
            },
          },
        }}
        onCellKeyDown={(params, event) => {
          if (event.key !== "Tab") return
          if (params.isEditable) return

          event.preventDefault()
          event.stopPropagation()

          const nextTarget = getNextTabTarget({
            currentField: params.field,
            row: params.row,
            isTender,
            isReferencePriceBank,
            tabOrder: GRID_TAB_ORDER,
          })

          if (!nextTarget) return

          requestAnimationFrame(() => {
            if (nextTarget.action === "focus") {
              params.api.setCellFocus(params.id, nextTarget.field)
              return
            }

            params.api.startCellEditMode({
              id: params.id,
              field: nextTarget.field,
            })
          })
        }}
        localeText={{
          ...DataGridLocaleText(),
          noRowsLabel: i18n.t("decomposition.noDecompositions"),
        }}
      />
    </Box>
  )
}

PartDetailsDataGrid.propTypes = {
  details: PropTypes.array.isRequired,
  setDetails: PropTypes.func.isRequired,
  partidaState: PropTypes.object.isRequired,
  bancoDePrecios: PropTypes.string,
  showDynamicElementButton: PropTypes.bool,
  onAddEmptyDetail: PropTypes.func.isRequired,
  isTender: PropTypes.bool,
}

export default PartDetailsDataGrid
